import { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAdminProperties, useDeleteProperty, useUpdateProperty, Property } from '@/hooks/useProperties';
import { PropertyFormDialog } from '@/components/admin/PropertyFormDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Search, Pencil, Trash2, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminProperties = () => {
  const { data: properties, isLoading } = useAdminProperties();
  const deleteProperty = useDeleteProperty();
  const updateProperty = useUpdateProperty();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const filteredProperties = properties?.filter(p =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.location_city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this property?')) {
      await deleteProperty.mutateAsync(id);
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    await updateProperty.mutateAsync({ id, is_active: !isActive });
  };

  const toggleFeatured = async (id: string, isFeatured: boolean) => {
    await updateProperty.mutateAsync({ id, is_featured: !isFeatured });
  };

  const handleEdit = (property: Property) => {
    setSelectedProperty(property);
    setDialogOpen(true);
  };

  const handleAddNew = () => {
    setSelectedProperty(null);
    setDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Properties</h1>
          <Button onClick={handleAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
        </div>

        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search properties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="bg-card rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProperties?.map((property) => (
                <TableRow key={property.id}>
                  <TableCell>
                    <img
                      src={property.main_image_url || '/placeholder.svg'}
                      alt={property.title}
                      className="h-16 w-16 object-cover rounded"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{property.title}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{property.property_type}</Badge>
                  </TableCell>
                  <TableCell>${property.price?.toLocaleString()}</TableCell>
                  <TableCell>{property.location_city}</TableCell>
                  <TableCell>
                    <Badge>{property.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={property.is_featured}
                      onCheckedChange={() => toggleFeatured(property.id, property.is_featured)}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={property.is_active}
                      onCheckedChange={() => toggleActive(property.id, property.is_active)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Link to={`/real-estate/${property.slug}`} target="_blank">
                      <Button variant="ghost" size="icon">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(property)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(property.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <PropertyFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          property={selectedProperty}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminProperties;
